
/**
 * Expandable.js v1.2.1
 * 
 * you can create an Expandable from any element you want a popover behaviour.
 * Requirements : 
 * * Set an `id` on the popover region
 * * Set an  `aria-controls` on every elements triggering the display/hide of the region
 * * Additionally, you can add the data-attribute `data-expanded="false"` (or true) on the region
 * * Have a beer.
 *
 * Features:
 * * every toggle buttons gets an (auto-updated) `aria-expanded` boolean attribute
 * * every region gets an (auto-updated) `data-expanded` boolean attribute
 * * use a [data-expandable-name] to make exclusive accordions (for every accordions having the same `data-expandable-name`, only one of them can be open at the same time)

> Note : By default, every popover regions are considered having `data-expanded="false"`, unless specified otherwise in the HTML.
*/ 

/**
Dev log
== 1.0.0 ==
First version
== 1.0.1 ==
Add onOpen() and onClose() callback hooks
...
== 1.1.0 ==
* Added addToggleButton(), removeToggleButton(), and destroy() methods
== 1.1.1 ==
* Fixed bad implementation of addToggleButton()
== 1.2.0 ==
* disabled removeToggleButton and destroy() (to be dully tested)
== 1.2.1 ==
* fix: previously couldn't call the close() and open() methods from outside

*/

/**
 * In ARIA, interactive menus, listboxes, trees, grids, and dialogs that appear on top of other content when triggered to appear are considered "popups". 
 * These popups are triggered by one or more interactive elements on the page. 
 * The availability and type of popup the interactive element will trigger should be identified with the aria-haspopup state.
 */

export class Expandable {
    // TODO: make it accessible: https://www.w3.org/WAI/ARIA/apg/patterns/accordion/
    // TODO: should I add a MutationObserver ?

    onOpen() {
        return
    }
    onClose() {
        return
    }
    
    get isExpanded() {
        return this.$region.dataset.expanded === 'true'
    }

    set isExpanded(e) {
        if (this.isDisabled === true) return
        
        // TODO: onBeforeOpen, onBeforeClose .then()
        // TODO: emit real DOM events ?
        
        // if (this.debug) console.log('triggered', this.$region)
        if (this.name && e.newState === true) this.neighbors.forEach(_$region => _$region.expandable?.close({ target: `exclusive region "${this.name}"` }))

        this.$region.dataset.expanded = e.newState
        this.$region.ariaHidden = !e.newState // TODO: use caution and test thoroughly
        this.toggleButtons.forEach($button => $button.setAttribute('aria-expanded', e.newState))
        
        if (e.newState) {
            this.onOpen(e.initiator)
        } else {
            this.onClose(e.initiator)
        }
    }
    constructor($region, params = undefined) {
        this.params = params
        
        this.isDisabled = false
        this.debug = this.params?.debug
        if (this.debug) console.log('new Expandable. selector:', $region)
        this.$region = typeof $region === 'string' ? document.body.querySelector($region) : $region
        if (!this.$region) console.error(`Cannot find region element with selector '${$region}'`)
        this.$region.expandable = this // expando attempt
        
        if (this.params?.on?.open) this.onOpen = (initiator) => this.params.on.open(this.$region, initiator)
        if (this.params?.on?.close) this.onClose = (initiator) => this.params.on.close(this.$region, initiator)

        this.name = this.$region.dataset.expandableName
        if (this.name) this.neighbors = document.querySelectorAll(`[data-expandable-name="${this.name}"]`)
        if (this.debug && this.neighbors) console.log('neighbors = ', this.neighbors)
        
        // handle buttons
        this.toggleAttribute = this.params?.toggleAttribute ?? 'aria-controls'
        this.toggleButtons = [ ...document.querySelectorAll(`[${this.toggleAttribute}~="${this.$region.id}"]`)]
        
        
        if (this.debug && this.toggleButtons.length === 0) console.error(`Cannot find toggle buttons for region with selector '${$region}'`)
        else if (this.debug) console.log(`toggle buttons for '${this.$region.id}:`, this.toggleButtons)
        
        // init buttons
        this.toggleButtons.forEach($btn => this.addToggleButton($btn))

        // init state (assert situation)
        this.isExpanded = {initiator: 'constructor', newState: (this.isExpanded ? true : false) }
    }
    
    
    
    addToggleButton($btn) {
        if ($btn.tagName !== 'BUTTON') $btn.setAttribute('role', 'button')
        if ($btn.tabIndex === -1) $btn.setAttribute('tab-index', 0)
        if (this.toggleAttribute !== 'aria-controls') $btn.setAttribute('aria-controls', this.$region.id)
        if (this.params?.hasPopup && $btn.dataset.expandableRole !== 'close') $btn.setAttribute('aria-haspopup', this.params.hasPopup)
        $btn.setAttribute('aria-expanded', this.isExpanded)
        
        if ($btn.dataset.expandableRole === 'open') $btn.addEventListener('click', this.open.bind(this), {capture: true, passive: true, once: false} || true)
        else if ($btn.dataset.expandableRole === 'close') $btn.addEventListener('click', this.close.bind(this), {capture: true, passive: true, once: false} || true)
        else $btn.addEventListener('click', this.onClick.bind(this), {capture: true, passive: true, once: false} || true)

        if (this.debug) console.log($btn, 'controls', this.$region)
    }
    
    /* currently disabled, to be tested */
    /*
    removeToggleButton($btn) {
        if ($btn.dataset.expandableRole === 'open') $btn.removeEventListener('click', this.open.bind(this))
        else if ($btn.dataset.expandableRole === 'close') $btn.removeEventListener('click', this.close.bind(this))
        else $btn.removeEventListener('click', this.onClick.bind(this))

        if (this.debug) console.log($btn, 'does not control anymore', this.$region)
        
        this.toggleButtons = this.toggleButtons.filter((element, index, array) => element !== $btn)
    }
    */
    
    /* currently disabled, to be tested */
    /*
    destroy() {
        if (this.debug) console.log('kill Expandable. selector:', $region)
        this.$region.expandable = null // remove expando
        
        
        this.onOpen = () => {return}
        this.onClose = () => {return}
    
        // init buttons
        this.toggleButtons.forEach($btn => this.removeToggleButton($btn))
    }
    */
    
    open(e = {currentTarget: undefined}) {
        if (this.debug) console.log('opening:', this.$region, 'source:', e.currentTarget, '(::open)')
        this.isExpanded = {newState: true, initiator: e.currentTarget}
    }
    close(e = {currentTarget: undefined}) {
        if (this.debug) console.log('closing:', this.$region, 'source:', e.currentTarget, '(::close)')
        this.isExpanded = {newState: false, initiator: e.currentTarget}
    }
    onClick(e = {currentTarget: undefined}) { // toggle
        if (this.debug) console.log('toggling', this.$region, 'source:', e.currentTarget, '(::onClick)')
        this.isExpanded = {initiator: e.currentTarget, newState: (this.isExpanded ? false : true)}
    }
    
    disable() {
        this.isDisabled = true
        this.toggleButtons.forEach($btn => {
            if ($btn.tagName !== 'BUTTON') $btn.setAttribute('aria-disabled', true)
            else $btn.disabled = true
        })
    }
    enable() {
        this.isDisabled = false
        this.toggleButtons.forEach($btn => {
            if ($btn.tagName !== 'BUTTON') $btn.removeAttribute('aria-disabled')
            else $btn.disabled = false
        })
    }
}


/*

Que faire si on veut qu'un toggleButton puisse changer de cible à la volée ?
--> Que faire si on veut qu'un toggleButton puisse être retiré à la volée ?
--> Que faire si on veut qu'un toggleButton puisse être créé à la volée ?


// option 1:
const $previousAccordionRegion = ''
const $newAccordionRegion = ''
const $btn = ''

$previousAccordionRegion.expandable.removeToggleButton($btn)
$newAccordionRegion.expandable.addToggleButton($btn)

// option 2:
mettre un data-attribut sur chaque toggle, et à l'initialisation de la page, watch tous les toggle (comme ça peu importe si le `aria-controls` change)
* souci : this.toggleButtons n'est du coup plus valide, il faut le rafraîchir aussi à chaque fois

// option 3:
1/ kill les deux Expandable
2/ change le `aria-controls`
3/ recrée les deux Expandable

$prevRegion.expandable = null
$nextRegion.expandable = null
$btn.setAttribute('aria-controls', $nextRegion.id)
new Expandable($nextRegion)

*/